// UI strings for the reminder notifications in Settings (Web Push), Finnish and English.
// Finnish follows docs/FINNISH-STYLE.md (no internal jargon, spaced en dash, times as 8.15).
// The notification text itself is written by the API (api/src/push/reminder.ts).
import { defineArea } from '../area';

export const remindersFi = {
  timeHint: 'Muistutukset lähtevät vartin välein. Jos valitset 8.07, muistutus tulee noin 8.15.',
  on: 'Muistutukset ovat käytössä tällä laitteella.',
  off: 'Muistutukset eivät ole käytössä tällä laitteella.',
  enable: 'Ota muistutukset käyttöön tällä laitteella',
  enableNote: 'Laite kysyy lupaa näyttää ilmoituksia. Valitse Salli.',
  disable: 'Poista käytöstä tällä laitteella',
  deniedIos:
    'Ilmoitukset on estetty tältä sovellukselta. Salli ne iPhonen asetuksista: Asetukset → Ilmoitukset → Valyrio udrir → Salli ilmoitukset. Palaa sitten tänne.',
  denied: 'Ilmoitukset on estetty tältä sivustolta. Salli ne selaimen sivustoasetuksista (osoiterivin vasemmasta reunasta) ja lataa sivu uudelleen.',
  needsInstall:
    'iPhonessa muistutukset toimivat vain Koti-valikkoon lisätyssä sovelluksessa. Avaa tämä sivu Safarissa, napauta Jaa ja valitse Lisää Koti-valikkoon. Avaa sitten Valyrio udrir Koti-valikon kuvakkeesta ja ota muistutukset käyttöön täällä.',
  needsUpdate: 'Muistutukset vaativat iOS:n 16.4 tai uudemman. Päivitä iPhone: Asetukset → Yleiset → Ohjelmiston päivitys.',
  unsupported: 'Tämä selain ei tue ilmoituksia.',
  failed: 'Muistutusten käyttöönotto ei onnistunut. Yritä uudelleen, kun yhteys toimii.',
  notConfigured: 'Ilmoitukset eivät ole vielä käytössä palvelimella.',
  test: 'Lähetä testi-ilmoitus',
  testing: 'Ilmoitus lähtee noin 5 sekunnin päästä…',
  testLead:
    'Testi lähettää saman muistutuksen kaikille laitteillesi noin 5 sekunnin päästä. Siirry sillä välin Koti-valikkoon tai lukitse puhelin: iPhone ei näytä ilmoitusta sovellukselle, joka on auki.',
  testWait: 'Odota hetki: testin voi lähettää kerran minuutissa.',
  testNone: 'Yhtään laitetta ei ole vielä lisätty. Ota muistutukset ensin käyttöön.',
  testFailed: 'Testiä ei saatu lähetettyä. Yritä uudelleen, kun yhteys toimii.',
  service: {
    apple: 'iPhone, iPad tai Mac',
    fcm: 'Chrome, Edge tai Android',
    mozilla: 'Firefox',
    windows: 'Windows',
    other: 'Muu laite',
  },
  thisDevice: 'tämä laite',
  resultOk: 'lähetetty, ilmoituksen pitäisi näkyä hetken päästä',
  resultRenewed: 'ilmoitukset piti ottaa uudelleen käyttöön – ne ovat taas päällä. Lähetä testi uudelleen hetken päästä.',
  resultRemoved: 'ilmoitukset pitää ottaa uudelleen käyttöön: avaa sovellus sillä laitteella, niin ne palaavat päälle.',
  resultFailed: (status: number) => (status ? `ei mennyt perille (virhe ${status})` : 'ei mennyt perille (ei yhteyttä)'),
  notArrived:
    'Eikö ilmoitus näy? Tarkista Asetukset → Ilmoitukset → Valyrio udrir. Tarkista myös, ettei Keskity-tila (esimerkiksi Älä häiritse tai Uni) ole päällä.',
};

export type RemindersMessages = typeof remindersFi;

export const remindersEn: RemindersMessages = {
  timeHint: 'Reminders go out every quarter hour. If you choose 8:07, the reminder comes at about 8:15.',
  on: 'Reminders are on for this device.',
  off: 'Reminders are off for this device.',
  enable: 'Turn on reminders for this device',
  enableNote: 'Your device asks permission to show notifications. Choose Allow.',
  disable: 'Turn off for this device',
  deniedIos:
    'Notifications are blocked for this app. Allow them in iPhone Settings: Settings → Notifications → Valyrio udrir → Allow Notifications. Then come back here.',
  denied: 'Notifications are blocked for this site. Allow them in the browser’s site settings (left end of the address bar) and reload the page.',
  needsInstall:
    'On iPhone, reminders work only in the app added to the Home Screen. Open this page in Safari, tap Share and choose Add to Home Screen. Then open Valyrio udrir from the Home Screen icon and turn reminders on here.',
  needsUpdate: 'Reminders need iOS 16.4 or later. Update the iPhone: Settings → General → Software Update.',
  unsupported: 'This browser does not support notifications.',
  failed: 'Could not turn on reminders. Try again when you are online.',
  notConfigured: 'Notifications are not available on the server yet.',
  test: 'Send a test notification',
  testing: 'The notification goes out in about 5 seconds…',
  testLead:
    'The test sends the same reminder to all your devices in about 5 seconds. Meanwhile go to the Home Screen or lock the phone: iPhone does not show a notification for the app that is open.',
  testWait: 'Wait a moment: the test can be sent once a minute.',
  testNone: 'No device has been added yet. Turn reminders on first.',
  testFailed: 'Could not send the test. Try again when you are online.',
  service: {
    apple: 'iPhone, iPad or Mac',
    fcm: 'Chrome, Edge or Android',
    mozilla: 'Firefox',
    windows: 'Windows',
    other: 'Other device',
  },
  thisDevice: 'this device',
  resultOk: 'sent, the notification should appear shortly',
  resultRenewed: 'notifications had to be turned on again – they are back on. Send the test again in a moment.',
  resultRemoved: 'notifications need to be turned on again: open the app on that device and they come back on.',
  resultFailed: (status: number) => (status ? `not delivered (error ${status})` : 'not delivered (no connection)'),
  notArrived: 'No notification? Check Settings → Notifications → Valyrio udrir. Also check that no Focus mode (such as Do Not Disturb or Sleep) is on.',
};

// Messages.reminders (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('reminders', remindersFi, remindersEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof remindersFi> {}
}
